// News — feed agregado del mercado + brief XAU del día + calendario económico
// embebido. Tres tabs.

import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useTheme } from '../contexts/ThemeContext';
import { AppLayout } from '../components/AppLayout';
import { Card } from '../components/ui/Card';
import { cn } from '../lib/cn';

interface NewsItem {
  id: string;
  title: string;
  link: string;
  source: string;
  sourceLabel: string;
  pubDate: string | null;
  summary: string | null;
  thumbnail: string | null;
}

interface FeedResponse {
  fetchedAt: string;
  count: number;
  items: NewsItem[];
}

interface XauEvent {
  id: string;
  label: string;
  region: 'US' | 'EU' | 'UK' | 'JP' | 'CN';
  utc: string;
  etDisplay: string;
  impact: 'high' | 'medium';
  xauHint: string;
}

interface XauWindow {
  label: string;
  utcStart: string;
  utcEnd: string;
  note: string;
}

interface XauAnalysis {
  biasLean: 'alcista' | 'bajista' | 'neutral' | 'volatil';
  headline: string;
  rationale: string;
  playbook: string;
  scenarios: { trigger: string; reaction: string; play: string }[];
  riskNote: string;
  model: string;
  generatedAt: string;
  disclaimer: string;
}

interface XauBriefResponse {
  generatedAt: string;
  dow: number;
  todayEvents: XauEvent[];
  relatedNews: NewsItem[];
  watchWindows: XauWindow[];
  aiAnalysis: XauAnalysis | null;
  notes: {
    primaryDrivers: string[];
    intradayTips: string[];
    weeklyBias: string;
  };
}

const BIAS_STYLE: Record<XauAnalysis['biasLean'], { label: string; cls: string }> = {
  alcista: { label: '▲ Alcista', cls: 'bg-win-DEFAULT/15 text-win-DEFAULT border-win-DEFAULT/30' },
  bajista: { label: '▼ Bajista', cls: 'bg-loss-DEFAULT/15 text-loss-DEFAULT border-loss-DEFAULT/30' },
  neutral: { label: '● Neutral', cls: 'bg-ink-subtle/10 text-ink-muted border-surface-border' },
  volatil: { label: '⚡ Volátil', cls: 'bg-warn-DEFAULT/15 text-warn-DEFAULT border-warn-DEFAULT/30' },
};

type Tab = 'market' | 'brief' | 'calendar';
type Filter = 'all' | 'crypto' | 'forex';

const FILTER_LABEL: Record<Filter, string> = {
  all: 'Todo',
  crypto: 'Cripto',
  forex: 'Forex / Commodities',
};

const INVESTING_CAL_URL =
  'https://sslecal2.investing.com?columns=exc_flags,exc_currency,exc_importance,exc_actual,exc_forecast,exc_previous&importance=2,3&features=datepicker,timezone,timeselector,filters&calType=week&timeZone=63&lang=10';

const chileTime = (iso: string): string =>
  new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));

const chileDate = (iso: string): string =>
  new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(iso));

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'recién';
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `hace ${d}d`;
  return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
}

const REGION_FLAG: Record<XauEvent['region'], string> = {
  US: '🇺🇸',
  EU: '🇪🇺',
  UK: '🇬🇧',
  JP: '🇯🇵',
  CN: '🇨🇳',
};

export default function News(): ReactElement {
  const [tab, setTab] = useState<Tab>('brief');
  const [filter, setFilter] = useState<Filter>('all');
  const { theme } = useTheme();

  const { data: feed, isLoading: feedLoading, isError: feedError, refetch: refetchFeed } = useQuery({
    queryKey: ['news-feed', filter],
    queryFn: () =>
      api<FeedResponse>(
        filter === 'all' ? '/news/feed?limit=50' : `/news/feed?category=${filter}&limit=50`,
      ),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
    enabled: tab === 'market',
  });

  const { data: brief, isLoading: briefLoading, refetch: refetchBrief } = useQuery({
    queryKey: ['news-xau-brief'],
    queryFn: () => api<XauBriefResponse>('/news/xau-brief'),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000,
    enabled: tab === 'brief',
  });

  const headline = useMemo(() => feed?.items[0] ?? null, [feed]);
  const rest = useMemo(() => feed?.items.slice(1) ?? [], [feed]);

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-10">
        <header className="mb-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] text-ink-subtle tracking-[0.22em] uppercase mb-2">
              Mercado · agenda
            </div>
            <h1 className="font-display text-4xl tracking-tight text-ink leading-tight">
              El día<span className="text-accent-500">,</span> en una pantalla.
            </h1>
            <p className="text-ink-muted mt-2 text-sm">
              Brief del oro + noticias del mercado + calendario económico.
            </p>
          </div>
        </header>

        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-surface-panel/60 border border-surface-border mb-6 flex-wrap">
          <TabBtn active={tab === 'brief'} onClick={() => setTab('brief')}>
            <span className="text-base leading-none">◇</span>
            Brief XAU
          </TabBtn>
          <TabBtn active={tab === 'market'} onClick={() => setTab('market')}>
            <span className="w-4 h-4">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 11v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                <path d="M4 11l2-6h12l2 6" />
                <path d="M9 14h6" />
              </svg>
            </span>
            Noticias
          </TabBtn>
          <TabBtn active={tab === 'calendar'} onClick={() => setTab('calendar')}>
            <span className="w-4 h-4">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="17" rx="2" />
                <path d="M3 9h18M8 2v4M16 2v4" />
              </svg>
            </span>
            Calendario
          </TabBtn>
        </div>

        {/* ─── Tab: Brief XAU ─── */}
        {tab === 'brief' && (
          <div className="space-y-6">
            {brief && (
              <div className="text-[11px] uppercase tracking-[0.18em] text-ink-subtle mb-2">
                {chileDate(brief.generatedAt)}
                <button
                  onClick={() => refetchBrief()}
                  className="ml-3 underline-offset-2 hover:underline text-ink-muted normal-case"
                >
                  refresh
                </button>
              </div>
            )}

            {briefLoading && (
              <Card padded={false} className="p-8">
                <div className="text-sm text-ink-subtle">Generando brief…</div>
              </Card>
            )}

            {brief && (
              <>
                {/* Análisis IA del día (Claude) */}
                {brief.aiAnalysis && (
                  <Card tone="accent" className="relative overflow-hidden">
                    <div className="flex items-start gap-3 flex-wrap">
                      <span
                        className={`shrink-0 px-3 py-1 rounded-full border text-xs font-bold ${
                          BIAS_STYLE[brief.aiAnalysis.biasLean].cls
                        }`}
                      >
                        {BIAS_STYLE[brief.aiAnalysis.biasLean].label}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-accent-500 font-semibold mb-1 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                          Análisis del día · IA
                        </div>
                        <h3 className="font-display text-xl text-ink leading-snug">
                          {brief.aiAnalysis.headline}
                        </h3>
                        <p className="text-sm text-ink-muted leading-relaxed mt-2">
                          {brief.aiAnalysis.rationale}
                        </p>
                      </div>
                    </div>

                    {/* Plan de acción */}
                    <div className="mt-4 p-3.5 rounded-lg bg-accent-500/8 border border-accent-500/25">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-accent-500 font-bold mb-1.5">
                        ▸ Cómo operarlo hoy
                      </div>
                      <p className="text-sm text-ink leading-relaxed">
                        {brief.aiAnalysis.playbook}
                      </p>
                    </div>

                    {brief.aiAnalysis.scenarios.length > 0 && (
                      <div className="grid sm:grid-cols-2 gap-2.5 mt-4">
                        {brief.aiAnalysis.scenarios.map((s, i) => (
                          <div
                            key={i}
                            className="p-3 rounded-lg bg-surface-panel/50 border border-surface-border"
                          >
                            <div className="text-xs font-bold text-ink leading-snug">
                              {s.trigger}
                            </div>
                            <p className="text-xs text-ink-muted leading-relaxed mt-1">
                              {s.reaction}
                            </p>
                            <p className="text-xs text-accent-500 leading-relaxed mt-1.5">
                              <span className="font-semibold">Jugada: </span>
                              {s.play}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 pt-3 border-t border-surface-border/60">
                      <p className="text-xs text-ink-muted leading-relaxed">
                        <span className="font-semibold text-ink">Riesgo: </span>
                        {brief.aiAnalysis.riskNote}
                      </p>
                      <p className="text-[10px] text-ink-faint mt-2 leading-relaxed">
                        {brief.aiAnalysis.disclaimer} · {brief.aiAnalysis.model}
                      </p>
                    </div>
                  </Card>
                )}

                <div className="grid lg:grid-cols-3 gap-4">
                  {/* Eventos del día */}
                  <div className="lg:col-span-2 space-y-4">
                    <Card>
                      <div className="text-[11px] font-semibold text-ink-subtle uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                        Eventos macro de hoy
                        <span className="ml-auto text-ink-faint normal-case tracking-normal">
                          horas Chile (Santiago)
                        </span>
                      </div>
                      {brief.todayEvents.length === 0 ? (
                        <p className="text-sm text-ink-muted py-4">
                          No hay eventos macro recurrentes de alto impacto hoy. Buen día para
                          análisis técnico sin sobresaltos por data.
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {brief.todayEvents.map((e) => (
                            <EventRow key={e.id} event={e} />
                          ))}
                        </div>
                      )}
                    </Card>

                    <Card>
                      <div className="text-[11px] font-semibold text-ink-subtle uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                        Noticias del oro hoy
                      </div>
                      {brief.relatedNews.length === 0 ? (
                        <p className="text-sm text-ink-muted py-2">
                          Sin notas con keywords oro/fed/inflación en las últimas 24h.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {brief.relatedNews.map((n) => (
                            <a
                              key={n.id}
                              href={n.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block p-3 rounded-lg border border-surface-border hover:border-accent-500/40 hover:bg-surface-panel/50 transition-all"
                            >
                              <div className="text-[10px] uppercase tracking-[0.16em] text-accent-500 font-semibold mb-1">
                                {n.sourceLabel} · {timeAgo(n.pubDate)}
                              </div>
                              <div className="text-sm font-semibold text-ink leading-snug">
                                {n.title}
                              </div>
                              {n.summary && (
                                <p className="text-xs text-ink-muted mt-1.5 line-clamp-2 leading-relaxed">
                                  {n.summary}
                                </p>
                              )}
                            </a>
                          ))}
                        </div>
                      )}
                    </Card>
                  </div>

                  {/* Aside derecho */}
                  <div className="space-y-4">
                    <Card tone="accent">
                      <div className="text-[11px] font-semibold text-ink-subtle uppercase tracking-[0.2em] mb-3">
                        Ventanas para vigilar
                      </div>
                      <div className="space-y-3">
                        {brief.watchWindows.map((w, i) => (
                          <div key={i} className="border-l-2 border-accent-500/50 pl-3">
                            <div className="text-xs font-bold text-ink">{w.label}</div>
                            <div className="font-mono text-sm text-accent-500 mt-0.5">
                              {chileTime(w.utcStart)} – {chileTime(w.utcEnd)}
                            </div>
                            <p className="text-[11px] text-ink-muted leading-snug mt-1">{w.note}</p>
                          </div>
                        ))}
                      </div>
                    </Card>

                    <Card>
                      <div className="text-[11px] font-semibold text-ink-subtle uppercase tracking-[0.2em] mb-3">
                        Drivers primarios XAU
                      </div>
                      <ul className="space-y-1.5">
                        {brief.notes.primaryDrivers.map((d, i) => (
                          <li key={i} className="text-xs text-ink-muted leading-relaxed flex gap-2">
                            <span className="text-accent-500 shrink-0">·</span>
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>

                    <Card>
                      <div className="text-[11px] font-semibold text-ink-subtle uppercase tracking-[0.2em] mb-3">
                        Intraday tips
                      </div>
                      <ul className="space-y-2">
                        {brief.notes.intradayTips.map((d, i) => (
                          <li key={i} className="text-xs text-ink-muted leading-relaxed flex gap-2">
                            <span className="text-accent-500 shrink-0 font-bold">→</span>
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>

                    {!brief.aiAnalysis && (
                      <Card tone="warn" padded={false} className="p-4">
                        <div className="text-[11px] font-semibold text-warn-DEFAULT uppercase tracking-[0.18em] mb-1">
                          Análisis IA
                        </div>
                        <p className="text-xs text-ink-muted leading-relaxed">
                          {brief.notes.weeklyBias}
                        </p>
                      </Card>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ─── Tab: Mercado ─── */}
        {tab === 'market' && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 flex-wrap">
              {(Object.keys(FILTER_LABEL) as Filter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={cn(
                    'px-3 h-8 rounded-full text-xs font-semibold uppercase tracking-wider transition-all border',
                    filter === f
                      ? 'bg-accent-500/12 border-accent-500/40 text-accent-500'
                      : 'border-surface-border text-ink-muted hover:text-ink hover:border-surface-strong',
                  )}
                >
                  {FILTER_LABEL[f]}
                </button>
              ))}
              <div className="ml-auto text-[11px] text-ink-subtle font-mono">
                {feed ? `${feed.count} items` : ' '}
                <button
                  onClick={() => refetchFeed()}
                  className="ml-3 underline-offset-2 hover:underline text-ink-muted"
                >
                  refresh
                </button>
              </div>
            </div>

            {feedLoading && (
              <div className="grid lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonNews key={i} large={i === 0} />
                ))}
              </div>
            )}

            {feedError && (
              <Card tone="loss">
                <div className="text-sm text-ink-muted">
                  No pudimos cargar el feed. Revisa tu conexión o intentá refresh.
                </div>
              </Card>
            )}

            {!feedLoading && feed && feed.items.length === 0 && (
              <Card className="text-center py-12">
                <p className="text-sm text-ink-subtle">Sin noticias por ahora.</p>
              </Card>
            )}

            {!feedLoading && headline && (
              <>
                <HeadlineCard item={headline} />
                {rest.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {rest.map((it) => (
                      <NewsCard key={it.id} item={it} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ─── Tab: Calendario ─── */}
        {tab === 'calendar' && (
          <Card padded={false} className="overflow-hidden">
            <header className="px-5 py-4 border-b border-surface-border flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-sm font-semibold text-ink">Calendario económico</h2>
                <p className="text-[11px] text-ink-subtle mt-0.5">
                  Eventos impacto medio + alto · zona horaria Chile · datos por Investing.com
                </p>
              </div>
              <a
                href="https://www.investing.com/economic-calendar/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-ink-muted hover:text-accent-500 transition-colors uppercase tracking-[0.18em]"
              >
                Abrir externo ↗
              </a>
            </header>
            <div
              className={cn(
                'investing-cal w-full',
                theme === 'dark' && 'investing-cal-dark',
              )}
              style={{ minHeight: 720 }}
            >
              <iframe
                src={INVESTING_CAL_URL}
                title="Calendario económico"
                width="100%"
                height="720"
                frameBorder="0"
                allowTransparency
                style={{ border: 0, display: 'block' }}
              />
            </div>
            <footer className="px-5 py-3 border-t border-surface-border text-[10px] text-ink-faint">
              Provisto por{' '}
              <a
                href="https://www.investing.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-accent-500 underline-offset-2 hover:underline"
              >
                investing.com
              </a>
              . Verificá horarios en su sitio antes de operar.
            </footer>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}

function EventRow({ event }: { event: XauEvent }): ReactElement {
  return (
    <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-start py-2 border-b border-surface-border/60 last:border-b-0">
      <div className="flex flex-col items-center min-w-[58px]">
        <div className="font-mono text-base font-bold text-accent-500 tabular-nums">
          {chileTime(event.utc)}
        </div>
        <div className="text-[9px] text-ink-faint font-mono mt-0.5">{event.etDisplay}</div>
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-base leading-none">{REGION_FLAG[event.region]}</span>
          <span className="text-sm font-semibold text-ink">{event.label}</span>
        </div>
        <p className="text-[11px] text-ink-muted leading-snug">{event.xauHint}</p>
      </div>
      <div className="shrink-0">
        <span
          className={cn(
            'text-[9px] uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded',
            event.impact === 'high'
              ? 'bg-loss-DEFAULT/10 border-loss-DEFAULT/40 text-loss-DEFAULT'
              : 'bg-warn-DEFAULT/10 border-warn-DEFAULT/40 text-warn-DEFAULT',
          )}
        >
          {event.impact === 'high' ? 'alto' : 'medio'}
        </span>
      </div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): ReactElement {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 px-4 h-9 rounded-full text-xs font-semibold uppercase tracking-wider transition-all',
        active
          ? 'bg-surface-elevated text-ink shadow-soft'
          : 'text-ink-muted hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function HeadlineCard({ item }: { item: NewsItem }): ReactElement {
  return (
    <a href={item.link} target="_blank" rel="noopener noreferrer" className="block group">
      <Card
        tone="default"
        padded={false}
        className="overflow-hidden grid md:grid-cols-2 gap-0 transition-all duration-200 group-hover:border-accent-500/40 group-hover:shadow-lift"
      >
        <div className="aspect-[16/9] md:aspect-auto bg-surface-panel relative overflow-hidden">
          {item.thumbnail ? (
            <img
              src={item.thumbnail}
              alt=""
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="absolute inset-0 bg-gold-soft opacity-30" />
          )}
          <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-md bg-stone-950/80 text-accent-200 backdrop-blur-sm">
            ★ Destacado
          </span>
        </div>
        <div className="p-6 flex flex-col justify-center">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] mb-3">
            <span className="font-semibold text-accent-500">{item.sourceLabel}</span>
            <span className="text-ink-faint">·</span>
            <span className="text-ink-subtle">{timeAgo(item.pubDate)}</span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl text-ink leading-tight group-hover:text-accent-500 transition-colors">
            {item.title}
          </h2>
          {item.summary && (
            <p className="text-sm text-ink-muted mt-3 line-clamp-3 leading-relaxed">
              {item.summary}
            </p>
          )}
          <div className="mt-4 text-[11px] uppercase tracking-[0.18em] text-ink-muted">
            Leer nota ↗
          </div>
        </div>
      </Card>
    </a>
  );
}

function NewsCard({ item }: { item: NewsItem }): ReactElement {
  return (
    <a href={item.link} target="_blank" rel="noopener noreferrer" className="block group">
      <Card
        padded={false}
        className="overflow-hidden h-full flex flex-col transition-all duration-200 group-hover:border-accent-500/40 group-hover:shadow-lift"
      >
        <div className="aspect-[16/9] bg-surface-panel relative overflow-hidden">
          {item.thumbnail ? (
            <img
              src={item.thumbnail}
              alt=""
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-ink-faint text-3xl">
              ◷
            </div>
          )}
        </div>
        <div className="p-4 flex-1 flex flex-col">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] mb-2">
            <span className="font-semibold text-accent-500 truncate">{item.sourceLabel}</span>
            <span className="text-ink-faint shrink-0">·</span>
            <span className="text-ink-subtle shrink-0">{timeAgo(item.pubDate)}</span>
          </div>
          <h3 className="font-semibold text-sm text-ink leading-snug line-clamp-3 group-hover:text-accent-500 transition-colors">
            {item.title}
          </h3>
        </div>
      </Card>
    </a>
  );
}

function SkeletonNews({ large }: { large?: boolean }): ReactElement {
  return (
    <Card
      padded={false}
      className={cn(
        'overflow-hidden',
        large && 'lg:col-span-3 md:grid md:grid-cols-2',
      )}
    >
      <div className={cn('aspect-[16/9] bg-surface-panel', large && 'md:aspect-auto')} />
      <div className="p-4 space-y-3">
        <div className="h-3 bg-surface-panel rounded w-1/3 animate-pulse" />
        <div className="h-4 bg-surface-panel rounded w-5/6 animate-pulse" />
        <div className="h-4 bg-surface-panel rounded w-2/3 animate-pulse" />
      </div>
    </Card>
  );
}
